import i18n from '../i18n';
import type { ApplicationStatus } from './runnerApiClient';

/**
 * The "starting line" tracker, derived from the application's pipeline status.
 * Order ruled by Rod 2026-10-01: review → profile → insurance → handbook →
 * video call → background check → banking → final review. No shadow ride.
 */
export type StepState = 'done' | 'now' | 'upcoming' | 'blocked';

export interface Step {
  key: string;
  label: string;
  sub?: string;
  state: StepState;
}

export interface RightNow {
  title: string;
  body: string;
}

const ORDER: { key: string; sub?: string }[] = [
  { key: 'apply' },
  { key: 'account' },
  { key: 'prescreen' },
  { key: 'profile' },
  { key: 'insurance' },
  { key: 'training' },
  { key: 'call', sub: 'callSub' },
  { key: 'checkr' },
  { key: 'banking', sub: 'bankingSub' },
  { key: 'final' },
  { key: 'golive' },
  { key: 'monitoring' },
];

const at = (key: string) => ORDER.findIndex((s) => s.key === key);

/** Index of the step that is "now" for a status; -1 = terminal/declined. */
function nowIndex(status: ApplicationStatus): number {
  switch (status) {
    case 'submitted': return at('prescreen');
    case 'in_review': return at('prescreen');
    case 'profile_setup': return at('profile');
    case 'insurance_review': return at('insurance');
    case 'training_ack': return at('training');
    case 'verification_call': return at('call');
    case 'checkr_pending': return at('checkr');
    case 'checkr_consider': return at('checkr');
    case 'checkr_clear': return at('banking');
    case 'checkr_waived': return at('banking');
    case 'banking_tax': return at('banking');
    case 'shadow_ride': return at('final');
    case 'pre_golive_review': return at('final');
    case 'approved': return at('golive');
    case 'live': return at('monitoring');
    default: return -1;
  }
}

export function buildSteps(status: ApplicationStatus, accountProvisioned: boolean): Step[] {
  const now = nowIndex(status);
  return ORDER.map((s, i) => {
    let state: StepState = i < now ? 'done' : i === now ? 'now' : 'upcoming';
    // Account is created at submit — done as soon as it exists (it sits before
    // pre-screen in the tracker per Rod 2026-08-25; the deck §3 frame still
    // draws it after).
    if (s.key === 'account' && accountProvisioned && i >= now) state = 'done';
    if (now === -1) state = i === 0 ? 'done' : 'blocked';
    return {
      key: s.key,
      label: i18n.t(`onboarding.step.${s.key}`),
      ...(s.sub ? { sub: i18n.t(`onboarding.step.${s.sub}`) } : {}),
      state,
    };
  });
}

export function doneCount(steps: Step[]): number {
  return steps.filter((s) => s.state === 'done').length;
}

const RIGHT_NOW_KEY: Record<ApplicationStatus, string> = {
  submitted: 'prescreen',
  in_review: 'prescreen',
  profile_setup: 'profile',
  insurance_review: 'insurance',
  training_ack: 'training',
  verification_call: 'call',
  checkr_pending: 'checkr',
  checkr_consider: 'consider',
  checkr_clear: 'cleared',
  checkr_waived: 'cleared',
  banking_tax: 'banking',
  shadow_ride: 'final',
  pre_golive_review: 'final',
  approved: 'approved',
  live: 'live',
  declined_prescreen: 'declinedPrescreen',
  declined_checkr: 'declinedCheckr',
  withdrawn: 'withdrawn',
};

export function rightNow(status: ApplicationStatus, firstName: string): RightNow {
  const k = RIGHT_NOW_KEY[status];
  return {
    title: i18n.t(`onboarding.now.${k}Title`),
    body: i18n.t(`onboarding.now.${k}Body`, { name: firstName }),
  };
}
export const METRO_LABEL: Record<string, string> = { charlotte: 'Charlotte', cincinnati: 'Cincinnati' };

/** Transport modes an applicant can pick. Car only for now (Rod 2026-10-01); runner-api refuses the rest. */
export const TRANSPORT_OPTIONS = [{ value: 'car', labelKey: 'car' }] as const;

/** Statuses where the portal offers self-service editing (mirrors runner-api). */
export const SELF_EDITABLE_STATUSES = ['submitted', 'in_review'];

/** Statuses where the insurance page is part of onboarding: from profile setup on. */
export const INSURANCE_OPEN_STATUSES: readonly ApplicationStatus[] = [
  'profile_setup', 'insurance_review', 'training_ack', 'verification_call',
  'checkr_pending', 'checkr_consider', 'checkr_clear', 'checkr_waived',
  'banking_tax', 'shadow_ride', 'pre_golive_review', 'approved', 'live',
];

/** Statuses after the background check has been handed off — banking is open from here. */
export const BANKING_OPEN_STATUSES: readonly ApplicationStatus[] = [
  'checkr_clear', 'checkr_waived', 'banking_tax', 'shadow_ride', 'pre_golive_review', 'approved', 'live',
];

/**
 * Where an applicant goes when they have a question, and where they must NOT be
 * sent.
 *
 * 🔒 Rod, 2026-09-15, choosing this over publishing a phone number or a postal
 * address: *"I like this idea: questions about your application"*.
 *
 * 🛑 THIS LINK NEVER OFFERS REPORT DETAILS. Our console renders no report
 * content by design, and an adverse-action notice has to tell the candidate that
 * the agency which produced the report did not make the decision and cannot
 * explain it. So report contents, corrections and disputes go to CHECKR — they
 * hold the data and own the dispute process — and only our own decision,
 * timeline and re-application come to us. A link promising "details on your
 * report" would be a promise we are required to redirect.
 *
 * ⚖️ The wording (translation.json → onboarding.help, EN + ES) is ON the
 * attorney review list (`docs/legal/ATTORNEY-REVIEW-LIST.md`, item 1) and has
 * NOT been reviewed by a lawyer. These are its keys, kept in one place so a
 * correction is a one-line change rather than a hunt through JSX.
 */
export const APPLICATION_HELP = {
  linkLabel: 'onboarding.help.linkLabel',
  cta: 'onboarding.help.cta',
  /** Shown only where a background-check report exists to be confused about. */
  reportNote: 'onboarding.help.reportNote',
} as const;

/**
 * Statuses that show the help link.
 *
 * 🪤 NOT just the declines. An applicant stuck in `checkr_pending` for a week,
 * or whose invitation quietly expired, has the same question and no route — and
 * that is where applicants are actually lost. A declined applicant mostly
 * accepts the answer; a stalled one just leaves.
 */
export const HELP_STATUSES: readonly ApplicationStatus[] = [
  'submitted', 'in_review', 'checkr_pending', 'checkr_consider',
  'declined_prescreen', 'declined_checkr', 'withdrawn',
];

/** Does this status involve a Checkr report the applicant might ask us about? */
export const showsReportNote = (status: ApplicationStatus): boolean =>
  status === 'checkr_pending' || status === 'checkr_consider' || status === 'declined_checkr';

export const showsApplicationHelp = (status: ApplicationStatus): boolean =>
  HELP_STATUSES.includes(status);
